import { useCallback, useEffect, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

import ErrorState from '@zenbusiness/zui/ErrorState';
import PageContainer from '@zenbusiness/zui/PageContainer';
import Text from '@zenbusiness/zui/Text';

import { useAccountSettings } from '#client/hooks/useAccountSettings';
import { isSocialAuth0Id } from '#client/utils/isSocialAuth0Id';

import * as Styled from './AccountSettings.style';
import { AccountHolder } from './components/AccountHolder';
import { ConnectWithPartners } from './components/ConnectWithPartners';
import { ManagePasswordModal } from './components/ManagePasswordModal';
import { OptOutModal } from './components/OptOutModal';

import { MODAL_PARAM_KEY } from '../Home/constants';

function AccountSettings() {
  const { data: accountInfo, error, loading, refetch } = useAccountSettings();

  const [searchParams, setSearchParams] = useSearchParams();
  const [currentModal, setCurrentModal] = useState(null);
  const { allPartnerOptOut, userAuth0Id } = accountInfo;
  const isSocialLogin = isSocialAuth0Id(userAuth0Id);

  const handleOpenModal = useCallback(
    (modal) => {
      setCurrentModal(modal);
      setSearchParams((params) => {
        params.set(MODAL_PARAM_KEY, modal);
        return params;
      });
    },
    [setSearchParams],
  );

  const handleCloseModal = () => {
    setCurrentModal(null);
    setSearchParams((params) => {
      params.delete(MODAL_PARAM_KEY);
      return params;
    });
  };

  useEffect(() => {
    const modalName = searchParams.get(MODAL_PARAM_KEY);
    if (!modalName || currentModal) {
      return;
    }
    handleOpenModal(modalName);
  }, [currentModal, handleOpenModal, searchParams]);

  if (error) {
    return <ErrorState />;
  }

  return (
    <PageContainer>
      <Styled.Header
        title={
          <Text component="p" size="2xl" family="secondary" weight="bold">
            Account settings
          </Text>
        }
      />
      <Styled.AccountCard>
        <AccountHolder
          isSocialLogin={isSocialLogin}
          account={accountInfo}
          loading={loading}
          onClickEdit={() => handleOpenModal('managePassword')}
        />
        <Styled.SectionDivider />
        <ConnectWithPartners account={accountInfo} loading={loading} onClickOptOut={() => handleOpenModal('optOut')} />
        <ManagePasswordModal
          account={accountInfo}
          isOpen={currentModal === 'managePassword'}
          onClose={() => handleCloseModal('managePassword')}
        />
        {!allPartnerOptOut && (
          <OptOutModal
            isOpen={currentModal === 'optOut'}
            onClose={() => handleCloseModal('optOut')}
            refresh={refetch}
          />
        )}
      </Styled.AccountCard>
    </PageContainer>
  );
}

export default AccountSettings;
